(function (root) {
  'use strict';
  var PARAM = 'omv_restore';

  function planId(item) {
    return Number(item.selling_plan || (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan.id)) || null;
  }

  function line(item) {
    return {id: Number(item.variant_id || item.id), quantity: Number(item.quantity), selling_plan: planId(item), properties: item.properties || {}};
  }

  function identity(item) {
    var properties = Object.keys(item.properties || {}).sort().map(function (key) { return [key, item.properties[key]]; });
    return JSON.stringify([item.id, item.selling_plan || null, properties]);
  }

  function signature(items) {
    return items.map(line).map(function (item) { return [identity(item), item.quantity]; }).sort(function (a, b) { return a[0].localeCompare(b[0]); });
  }

  function validItems(items) {
    if (!Array.isArray(items) || !items.length || items.length > 250) return false;
    var seen = new Set();
    return items.every(function (item) {
      var key = identity(item);
      if (seen.has(key)) return false;
      seen.add(key);
      return Number.isSafeInteger(item.id) && item.id > 0 && Number.isSafeInteger(item.quantity) && item.quantity > 0 && item.quantity <= 9999 && (item.selling_plan === null || Number.isSafeInteger(item.selling_plan) && item.selling_plan > 0);
    });
  }

  function createRecovery(cart) {
    var items = (cart.items || []).map(function (item) {
      var value = line(item);
      if (Object.keys(value.properties).length) throw new Error('Custom item properties require the original cart');
      return {id: value.id, quantity: value.quantity, selling_plan: value.selling_plan,
        title: String(item.product_title || item.title || 'METHL item').slice(0, 140),
        plan_label: String(item.selling_plan_allocation && item.selling_plan_allocation.selling_plan.name || '').slice(0, 100)};
    });
    if (!validItems(items)) return null;
    var json = JSON.stringify({version: 1, items: items});
    var bytes = new TextEncoder().encode(json);
    var encoded = btoa(Array.from(bytes, function (b) { return String.fromCharCode(b); }).join('')).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    return encoded.length <= 6000 ? encoded : null;
  }

  function decodeRecovery(value) {
    if (typeof value !== 'string' || value.length > 6000 || !/^[A-Za-z0-9_-]+$/.test(value)) throw new Error('Invalid recovery link');
    var binary = atob(value.replace(/-/g, '+').replace(/_/g, '/'));
    var payload = JSON.parse(new TextDecoder('utf-8', {fatal: true}).decode(Uint8Array.from(binary, function (c) { return c.charCodeAt(0); })));
    if (payload.version !== 1 || !validItems(payload.items)) throw new Error('Invalid saved cart');
    return payload.items.map(function (item) {
      return {id: item.id, quantity: item.quantity, selling_plan: item.selling_plan,
        title: String(item.title || 'METHL item').slice(0, 140), plan_label: String(item.plan_label || '').slice(0, 100)};
    });
  }

  async function applyItems(api, target) {
    var current = await api('cart.js');
    var desired = new Map(target.map(line).map(function (item) { return [identity(item), item]; }));
    var existing = new Set(current.items.map(line).map(identity));
    var missing = Array.from(desired.values()).filter(function (item) { return !existing.has(identity(item)); });
    if (missing.length) await api('cart/add.js', {items: missing.map(function (item) {
      var value = {id: item.id, quantity: item.quantity, properties: item.properties};
      if (item.selling_plan) value.selling_plan = item.selling_plan;
      return value;
    })});
    current = await api('cart.js');
    var updates = {};
    current.items.forEach(function (item) { var wanted = desired.get(identity(line(item))); updates[item.key] = wanted ? wanted.quantity : 0; });
    if (Object.keys(updates).length) await api('cart/update.js', {updates: updates});
    var saved = await api('cart.js');
    if (JSON.stringify(signature(saved.items)) !== JSON.stringify(signature(target))) throw new Error('Saved cart did not match');
    return saved;
  }

  async function replaceCart(api, target) {
    var previous = await api('cart.js');
    try { return await applyItems(api, target); }
    catch (error) {
      try { await applyItems(api, previous.items); error.previousCartRestored = true; }
      catch (_) { error.previousCartRestored = false; }
      throw error;
    }
  }

  var functions = {line: line, signature: signature, createRecovery: createRecovery, decodeRecovery: decodeRecovery, replaceCart: replaceCart};
  if (typeof module === 'object' && module.exports) { module.exports = functions; return; }
  if (!root || root.__omvCartRecoveryLoaded) return;
  root.__omvCartRecoveryLoaded = true;
  var document = root.document;
  var route = root.Shopify && root.Shopify.routes && root.Shopify.routes.root || '/';
  var originalFetch = root.fetch;
  var timer, busy = false, pending = new URL(root.location.href).searchParams.has(PARAM), lastSent = '';

  async function api(path, data) {
    var response = await originalFetch.call(root, route + path, {credentials: 'same-origin', method: data ? 'POST' : 'GET',
      headers: {'Accept': 'application/json', 'Content-Type': 'application/json'}, body: data ? JSON.stringify(data) : undefined});
    if (!response.ok) throw new Error('The store could not update this cart');
    return response.json();
  }

  function permitted() {
    var privacy = root.Shopify && root.Shopify.customerPrivacy;
    return !!(privacy && typeof privacy.analyticsProcessingAllowed === 'function' && typeof privacy.marketingAllowed === 'function' && privacy.analyticsProcessingAllowed() && privacy.marketingAllowed());
  }

  function deadline(promise) {
    return Promise.race([promise, new Promise(function (_, reject) { root.setTimeout(function () { reject(new Error('Tracking unavailable')); }, 5000); })]);
  }

  async function recordCart() {
    if (busy || pending || !permitted() || root.Shopify && (root.Shopify.designMode || root.Shopify.theme && root.Shopify.theme.role !== 'main')) return;
    var sdk = root.klaviyo;
    if (!sdk || typeof sdk.isIdentified !== 'function' || typeof sdk.identify !== 'function' || typeof sdk.track !== 'function') return;
    try {
      if (!await deadline(sdk.isIdentified())) return;
      var cart = await api('cart.js');
      var fingerprint = JSON.stringify(signature(cart.items));
      if (fingerprint === lastSent) return;
      var token = null;
      try { token = createRecovery(cart); } catch (_) {}
      var url = token ? 'https://onlymultivitamin.com/cart?' + PARAM + '=' + token : null;
      var items = cart.items.map(function (item) { return {VariantID: Number(item.variant_id), Quantity: item.quantity,
        SellingPlanID: planId(item), ProductID: Number(item.product_id), ProductName: item.product_title || item.title,
        Price: item.final_price / 100, LineTotal: item.final_line_price / 100}; });
      var properties = {OMV_CartRecoveryURL: url, OMV_FullCartRecoveryAvailable: !!url, OMV_CartItems: items,
        OMV_CartItemCount: cart.item_count, OMV_CartUpdatedAt: new Date().toISOString(), OMV_CartSchemaVersion: 1};
      await deadline(sdk.identify(properties));
      await deadline(sdk.track('OMV Cart Snapshot', {CartRecoveryURL: url, FullCartRecoveryAvailable: !!url,
        CartItems: items, ItemCount: cart.item_count, Currency: cart.currency, SchemaVersion: 1, '$value': cart.total_price / 100}));
      lastSent = fingerprint;
    } catch (_) {}
  }

  function schedule() { root.clearTimeout(timer); timer = root.setTimeout(recordCart, 750); }
  function mutationURL(value) {
    try { var url = new URL(value, root.location.origin); return url.origin === root.location.origin && /\/cart\/(add|change|update|clear)(\.js)?$/.test(url.pathname); }
    catch (_) { return false; }
  }

  root.fetch = function (input) {
    var promise = originalFetch.apply(this, arguments);
    if (mutationURL(typeof input === 'string' ? input : input && input.url)) promise.then(function (response) { if (response.ok) schedule(); }, function () {});
    return promise;
  };
  if (root.XMLHttpRequest) {
    var open = root.XMLHttpRequest.prototype.open, send = root.XMLHttpRequest.prototype.send, marker = Symbol('omvCartMutation');
    root.XMLHttpRequest.prototype.open = function (method, url) { this[marker] = mutationURL(url); return open.apply(this, arguments); };
    root.XMLHttpRequest.prototype.send = function () {
      if (this[marker]) this.addEventListener('loadend', function () { if (this.status >= 200 && this.status < 300) schedule(); }, {once: true});
      return send.apply(this, arguments);
    };
  }

  function cleanURL() { var url = new URL(root.location.href); url.searchParams.delete(PARAM); root.history.replaceState(null, '', url.pathname + url.search + url.hash); }

  function recoveryPanel() {
    if (!pending || !/\/cart\/?$/.test(root.location.pathname)) return;
    var items;
    try { items = decodeRecovery(new URL(root.location.href).searchParams.get(PARAM)); }
    catch (_) { pending = false; cleanURL(); return; }
    var section = document.createElement('section');
    section.id = 'omv-saved-cart'; section.setAttribute('aria-labelledby', 'omv-saved-cart-title');
    section.style.cssText = 'max-width:1000px;margin:24px auto;padding:24px;border:1px solid #b5c8b4;border-radius:8px;background:#f4f7ef;color:#123a25;font:inherit';
    var heading = document.createElement('h2'); heading.id = 'omv-saved-cart-title'; heading.textContent = 'Restore your saved cart'; section.append(heading);
    var message = document.createElement('p'); message.textContent = 'Restoring will replace the items currently in your cart with these saved items and delivery choices. You can review current prices and availability in your cart.'; section.append(message);
    var list = document.createElement('ul'); items.forEach(function (item) { var li = document.createElement('li'); li.textContent = item.title + ' × ' + item.quantity + (item.selling_plan ? ' · ' + (item.plan_label || 'Subscription') : ' · One-time purchase'); list.append(li); }); section.append(list);
    var restore = document.createElement('button'), keep = document.createElement('button');
    restore.type = keep.type = 'button'; restore.textContent = 'Restore saved cart'; keep.textContent = 'Keep current cart';
    [restore, keep].forEach(function (button) { button.style.cssText = 'font:inherit;padding:12px 18px;margin:8px 12px 0 0;border:1px solid #123a25;border-radius:5px;cursor:pointer'; });
    restore.style.background = '#123a25'; restore.style.color = '#fff'; keep.style.background = '#fff'; keep.style.color = '#123a25';
    var status = document.createElement('p'); status.setAttribute('role', 'status'); status.setAttribute('aria-live', 'polite');
    section.append(restore, keep, status);
    restore.addEventListener('click', async function () {
      restore.disabled = keep.disabled = true; busy = true; section.setAttribute('aria-busy', 'true'); status.textContent = 'Restoring your cart…';
      try { await replaceCart(api, items); root.location.replace(route + 'cart'); }
      catch (error) {
        status.textContent = error.previousCartRestored ? 'We could not restore these items. Your previous cart was kept. Please try again or contact hello@onlymultivitamin.com.' : 'We could not fully restore this cart. Please review your cart before checkout or contact hello@onlymultivitamin.com.';
        restore.disabled = keep.disabled = false; busy = false; section.removeAttribute('aria-busy');
      }
    });
    keep.addEventListener('click', function () { pending = false; cleanURL(); section.remove(); schedule(); });
    var main = document.querySelector('main') || document.getElementById('MainContent') || document.body;
    main.insertBefore(section, main.firstChild);
  }

  function start() {
    recoveryPanel();
    if (root.Shopify && typeof root.Shopify.loadFeatures === 'function' && !root.Shopify.customerPrivacy) root.Shopify.loadFeatures([{name: 'consent-tracking-api', version: '0.2'}], function (error) { if (!error) schedule(); });
    schedule();
  }
  document.addEventListener('visitorConsentCollected', schedule);
  root.addEventListener('klaviyoForms', function () { [1000, 3000, 6000].forEach(function (delay) { root.setTimeout(schedule, delay); }); });
  root.addEventListener('pageshow', schedule);
  root.addEventListener('load', schedule, {once: true});
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once: true}); else start();
})(typeof window === 'undefined' ? null : window);
